import { Dispatch, FC, SetStateAction, createContext, useContext, useEffect, useState } from 'react';

import { BreadcrumbItem, BreadcrumbType } from '../components/breadcrumb/breadcrumb';
import { useLocale } from '../helpers/LangHelper';
import { getBusinessHubMenu } from '../shared/commonPageDetails';
import { BusinessHubMenuItemType } from '../types/BusinessIndustries';
import { Category } from '../types/Category';
import { useNotification } from './NotificationContext';
import { WebFooterItemType } from '@/types/WebFooter';

export enum BusinessTheme {
    DEFAULT = 'default',
    MARKETING_RESOURCE = 'marketing resource',
    BUSINESS_HUB = 'business hub',
}

export interface FooterItems {
    global: WebFooterItemType[];
    regional: WebFooterItemType[];
}

interface BusinessLayoutContextProps {
    theme?: BusinessTheme;

    breadcrumbs: BreadcrumbItem[];
    breadcrumbType: BreadcrumbType;
    hideBreadcrumbs?: boolean;
    footerItems: FooterItems;
    categories: Category[];
    businessHubMenuData: BusinessHubMenuItemType[];

    setBreadcrumbs?: Dispatch<SetStateAction<BreadcrumbItem[]>>;
    setBreadcrumbType?: Dispatch<SetStateAction<BreadcrumbType>>;
    setHideBreadcrumbs?: Dispatch<SetStateAction<boolean>>;
    changeTheme?: Dispatch<SetStateAction<BusinessTheme | undefined>>;
}

const defaultBreadcrumbs: BreadcrumbItem[] = [
    {
        name: 'Business',
        link: '/business',
    },
];

const BusinessLayoutContext = createContext<BusinessLayoutContextProps>({
    breadcrumbs: defaultBreadcrumbs,
    breadcrumbType: 'dark',
    footerItems: {
        global: [],
        regional: [],
    },
    categories: [],
    businessHubMenuData: [],
});

interface Props {
    theme?: BusinessTheme;
    footerItems: FooterItems;
    categories: Category[];
    businessHubMenuData: BusinessHubMenuItemType[];
}

export const BusinessLayoutContextWrapper: FC<Props> = (props) => {
    const { theme: themeProps, footerItems, categories, businessHubMenuData, children } = props;

    const { locale } = useLocale();

    const [theme, setTheme] = useState<BusinessTheme>();
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
    const [breadcrumbType, setBreadcrumbType] = useState<BreadcrumbType>('dark');
    const [hideBreadcrumbs, setHideBreadcrumbs] = useState<boolean>(false);
    const [menuData, setMenuData] = useState<BusinessHubMenuItemType[]>(businessHubMenuData);
    const notification = useNotification();

    useEffect(() => {
        setTheme(themeProps);
        notification.setTheme(themeProps);
    }, [themeProps]);

    useEffect(() => {
        if (!businessHubMenuData) {
            getBusinessHubMenu(locale).then(({ menuData }) => {
                setMenuData(menuData);
            });
        }
    }, [businessHubMenuData, locale]);

    return (
        <BusinessLayoutContext.Provider
            value={{
                theme,
                breadcrumbs,
                breadcrumbType,
                hideBreadcrumbs,
                footerItems,
                categories,
                businessHubMenuData: menuData,
                setBreadcrumbs,
                setBreadcrumbType,
                setHideBreadcrumbs,
                changeTheme: setTheme,
            }}
        >
            {children}
        </BusinessLayoutContext.Provider>
    );
};

export const useBusinessLayout = () => useContext(BusinessLayoutContext);
